import {
  Button,
  colors,
  Label,
  LabeledInput,
  LabeledRadio,
  Text,
  Textarea,
  Typography,
} from "@commonsku/styles";
import React, { useCallback } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { get } from "lodash";
import useMobileScreen from "../hooks/useMobileScreen";
import { mobileScreenSize, oauth } from "../utils";
import { SalesOrderStatus } from "../models/Status";
import OrderTemplateBanner from "../components/project/sales-order/OrderTemplateBanner";
import {
  OrderStatusRadioOption,
  PageMode,
  ATTRIBUTES,
} from "../components/project/sales-order/types";
import { IOrder, Order } from "../interfaces/Order";

interface SalesOrderApproveProps {
  order: Order;
}

const SalesOrderApproveContainer = styled.div`
  width: 100%;
  height: auto;
  min-height: 100vh;
  background: ${colors.neutrals[20]};
  overflow: auto;

  &&& {
    margin-top: 0px;
    padding-left: 34px;
    padding-right: 34px;
  }
`;

const SalesOrderApproveFormContainer = styled.div`
  gap: 16px;
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};

  &&& {
    padding: 32px;
    margin-bottom: 34px;
  }
  .heading {
    font-size: 20px;
    color: ${colors.neutrals[90]};
    margin-bottom: 0;
  }
`;

const SalesOrderAttributes = styled.li`
  list-style-type: disc;
  font-weight: 100;
`;

const AttributesContainer = styled.div`
  width: 25%;
  display: flex;

  ul {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 8px;
    height: 120px;
    width: 100%;
  }

  @media (max-width: ${mobileScreenSize}px) {
    height: auto;
    width: 100%;

    ul {
      flex-direction: row;
      height: auto;
    }
  }
  li {
    width: 200px;
  }

  li > p {
    margin-bottom: 0;
  }
`;

const FormButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  gap: 8px;
`;

const FlexContainer = styled.div<{
  flexDirection: string;
  gap?: string;
  justifyContent?: string;
}>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection};
  gap: ${(props) => props.gap || "16px"};
  justify-content: ${(props) => props.justifyContent || "flex-start"};
`;

const InputContainer = styled.div`
  width: 40%;

  @media (max-width: ${mobileScreenSize}px) {
    width: 100%;
  }
`;

const SubmitContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const SubmitDetails = ({ fullName }: { fullName: string }) => {
  return (
    <SubmitContainer>
      <Typography.H1 className="heading">Thank you, {fullName}!</Typography.H1>
      <Typography.Text>
        Your information has been submitted. Someone from our account team will
        contact you shortly.
      </Typography.Text>
    </SubmitContainer>
  );
};

const SalesOrderApprove: React.FC<SalesOrderApproveProps> = () => {
  const [fullName, setFullName] = React.useState<string>("");
  const [notes, setNotes] = React.useState<string>("");
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const queryParams = new URLSearchParams(window.location.search);
  const defaultRadioOption =
    queryParams.has("change") && queryParams.get("change") === "true"
      ? OrderStatusRadioOption.REQUEST_CHANGE
      : OrderStatusRadioOption.APPROVED;
  const [radioOption, setRadioOption] =
    React.useState<OrderStatusRadioOption>(defaultRadioOption);

  const buttonDisabled = fullName === "";
  const orderDetails = useSelector((state) => {
    return get(state, "temp.order");
  }) as IOrder;

  const approved_ip = useSelector((state) => {
    return get(state, "temp.ip");
  }) as string;

  const orderId = useSelector((state) => {
    return get(state, "temp.orderId");
  }) as string;

  const pageMode = PageMode.SUBMIT;

  const isMobile = useMobileScreen();

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };

  //TODO: CORE-1180 set the statusId based on the page mode
  const handleSubmit = useCallback(async () => {
    let statusId = SalesOrderStatus.ClientApproved;

    if (radioOption === OrderStatusRadioOption.REQUEST_CHANGE) {
      statusId = SalesOrderStatus.ClientChangeRequested;
    }

    try {
      const res = await oauth("PUT", `approval/${orderId}`, {
        status_id: statusId,
        approved_by: fullName,
        approved_notes: notes,
        approved_ip,
      });

      setSubmitted(!!res.json.success);
      if (!res.json.success) console.error("Error approving order:", res);
    } catch (e) {
      console.error("Error approving order:", e);
    }
  }, [fullName, notes, orderId, approved_ip, radioOption]);

  return (
    <SalesOrderApproveContainer>
      <OrderTemplateBanner
        mode={pageMode}
        orderTemplate={orderDetails.order_template}
        clientName={orderDetails.client_name}
        orderType={orderDetails.order_type}
        formNumber={orderDetails.form_number}
      />
      {submitted ? (
        <SubmitDetails fullName={fullName} />
      ) : (
        <SalesOrderApproveFormContainer>
          <Text className="heading">
            Important! Please read the following carefully!
          </Text>
          <Typography.Text>
            By approving the order, you are acknowledging you have thoroughly
            reviewed all of the following aspects of this order:
          </Typography.Text>
          <AttributesContainer>
            <ul>
              {ATTRIBUTES.map((attribute) => (
                <SalesOrderAttributes key={attribute}>
                  <Typography.Text>{attribute}</Typography.Text>
                </SalesOrderAttributes>
              ))}
            </ul>
          </AttributesContainer>
          <Text className="heading">
            Please fill out the form below to put the order into production.
          </Text>
          <FlexContainer flexDirection={isMobile ? "column" : "row"}>
            <InputContainer>
              <LabeledInput
                labelOnTop
                label="Your Name:"
                name="full-name"
                placeholder="Enter your full name"
                value={fullName}
                onChange={handleNameChange}
              />
            </InputContainer>
            {pageMode === PageMode.SUBMIT && (
              <FlexContainer flexDirection="column" gap="8px">
                <Label>Change Order Status to:</Label>
                <FlexContainer flexDirection={isMobile ? "column" : "row"}>
                  <LabeledRadio
                    label="Approved"
                    value={radioOption}
                    checked={radioOption === OrderStatusRadioOption.APPROVED}
                    onChange={() =>
                      setRadioOption(OrderStatusRadioOption.APPROVED)
                    }
                  />
                  <LabeledRadio
                    label="Request Change"
                    value={radioOption}
                    checked={
                      radioOption === OrderStatusRadioOption.REQUEST_CHANGE
                    }
                    onChange={() =>
                      setRadioOption(OrderStatusRadioOption.REQUEST_CHANGE)
                    }
                  />
                </FlexContainer>
              </FlexContainer>
            )}
          </FlexContainer>

          <Label>Additional Notes:</Label>
          <Textarea
            placeholder="Enter additional notes"
            rows={5}
            value={notes}
            name="additional-notes"
            onChange={handleNotesChange}
          />
          <FormButtonContainer>
            {pageMode === PageMode.SUBMIT && (
              <Button
                variant="primary"
                disabled={buttonDisabled}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            )}
          </FormButtonContainer>
        </SalesOrderApproveFormContainer>
      )}
    </SalesOrderApproveContainer>
  );
};

export default SalesOrderApprove;
